// Consent state (docs/10 §4).
//
// Dev mode is already an explicit, deliberate unlock, so auto-send defaults ON
// when dev mode is on and can be turned off at any time. Outside dev mode
// NOTHING is ever uploaded automatically — a severe-failure popup is automatic,
// the upload it offers is not.
//
// Two obligations ride with "automatic", and both are enforced here rather than
// left to a host: `disclosurePending()` goes true the moment dev mode unlocks
// (the dialog belongs at the decision that causes the sending, not at the first
// send, which would arrive later and unexplained), and nothing this module
// permits can be sent without `preview()` being able to show it first.

import type { KvStore } from '../../storage/kv.js';

export const DIAG_CONSENT_KEY = 'merkury.diag.consent.v1';

/** How the install rotates its optional id (§3) — off unless dev mode chose it. */
export const INSTALL_ID_ROTATION_MS = 7 * 24 * 60 * 60 * 1000;

export type ConsentMode =
  /** Uploads happen on their own schedule. */
  | 'auto'
  /** Nothing goes automatically; an explicit per-report tap still sends. */
  | 'ask'
  /** The toggle is off. Nothing goes, with or without a prompt. */
  | 'off';

interface ConsentState {
  v: 1;
  /** User's answer to the auto-send toggle. Absent = never answered. */
  autoSend?: boolean;
  /** Whether the dev-unlock disclosure has been shown for this unlock. */
  disclosedAt?: number;
  /** Optional rotating install id (§3) — absent unless dev mode opted in. */
  installId?: string;
  installIdAt?: number;
}

export interface ConsentOptions {
  kv: KvStore;
  key?: string;
  now?: () => number;
}

export class Consent {
  private readonly kv: KvStore;
  private readonly key: string;
  private readonly now: () => number;
  private state: ConsentState = { v: 1 };
  private devMode = false;
  private loaded = false;

  constructor(options: ConsentOptions) {
    this.kv = options.kv;
    this.key = options.key ?? DIAG_CONSENT_KEY;
    this.now = options.now ?? Date.now;
  }

  async load(): Promise<void> {
    if (this.loaded) return;
    this.loaded = true;
    try {
      const stored = await this.kv.get<ConsentState>(this.key);
      if (stored && typeof stored === 'object') this.state = { ...stored, v: 1 };
    } catch {
      // A default-safe state (nothing uploads) is the right failure here.
    }
  }

  /**
   * Mirror the dev-unlock flag. A FRESH unlock arms the disclosure dialog; a
   * re-assertion of a state we already knew does not, so the dialog fires on
   * the unlock and not on every boot afterwards.
   */
  async setDevMode(on: boolean): Promise<void> {
    if (this.devMode === on) return;
    this.devMode = on;
    if (!on) {
      // Leaving dev mode clears the disclosure so a later unlock discloses
      // again — and clears any install id, which existed only for dev mode.
      delete this.state.disclosedAt;
      delete this.state.installId;
      delete this.state.installIdAt;
      await this.save();
    }
  }

  isDevMode(): boolean {
    return this.devMode;
  }

  /** True while dev mode is on and the unlock disclosure has not been shown. */
  disclosurePending(): boolean {
    return this.devMode && this.state.disclosedAt === undefined;
  }

  async markDisclosed(): Promise<void> {
    this.state.disclosedAt = this.now();
    await this.save();
  }

  /** The user's explicit answer, or undefined if never asked. */
  autoSendChoice(): boolean | undefined {
    return this.state.autoSend;
  }

  /** Auto-send is ON by default under dev mode; the user's answer always wins. */
  autoSend(): boolean {
    return this.state.autoSend ?? this.devMode;
  }

  async setAutoSend(on: boolean): Promise<void> {
    this.state.autoSend = on;
    await this.save();
  }

  mode(): ConsentMode {
    if (this.state.autoSend === false) return 'off';
    // Outside dev mode nothing is ever uploaded automatically (§4), whatever
    // the toggle says — the toggle can only ever subtract here.
    if (!this.devMode) return 'ask';
    return this.autoSend() ? 'auto' : 'off';
  }

  /** May the uploader send `report` without asking anyone? */
  allowsAutomatic(): boolean {
    return this.mode() === 'auto';
  }

  /** May a report the user explicitly consented to be sent? Only when the
   *  toggle has not been turned off outright. */
  allowsConsented(): boolean {
    return this.mode() !== 'off';
  }

  /**
   * The rotating install id (§3), for the longitudinal questions that genuinely
   * need one. Dev mode only, opt-in, rotates every 7 days, resettable — and
   * returns undefined in every other case, which is the default.
   */
  installId(): string | undefined {
    if (!this.devMode) return undefined;
    const at = this.state.installIdAt;
    if (this.state.installId === undefined || at === undefined) return undefined;
    return this.now() - at > INSTALL_ID_ROTATION_MS ? undefined : this.state.installId;
  }

  /** Opt in (or out) of the rotating install id. */
  async setInstallId(id: string | null): Promise<void> {
    if (id === null) {
      delete this.state.installId;
      delete this.state.installIdAt;
    } else {
      this.state.installId = id;
      this.state.installIdAt = this.now();
    }
    await this.save();
  }

  /** True when the id exists but has aged out and should be re-minted. */
  installIdExpired(): boolean {
    const at = this.state.installIdAt;
    return (
      this.devMode &&
      this.state.installId !== undefined &&
      at !== undefined &&
      this.now() - at > INSTALL_ID_ROTATION_MS
    );
  }

  private async save(): Promise<void> {
    await this.kv.put(this.key, this.state).catch(() => undefined);
  }
}
